import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  CardGiftcard as TrialIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';
import { getAllFilmSubscribers, updateUserFilmTier, grantTrialAccess } from '../../lib/services/subscriptionService';
import { FILM_TIERS, FILM_TIER_NAMES, SUBSCRIPTION_STATUS } from '../../lib/filmSubscriptionTiers';

/**
 * FilmSubscriptions Admin Page
 * 
 * Manage individual Film subscriptions (not companies)
 */
const FilmSubscriptions = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Edit dialog state
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [editData, setEditData] = useState({
    tier: '',
    status: '',
    expiresAt: ''
  });
  
  // Trial dialog state
  const [trialDialog, setTrialDialog] = useState({ open: false, user: null });
  const [trialData, setTrialData] = useState({
    tier: FILM_TIERS.PROFESSIONAL,
    days: 14
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {};
      if (filterTier) filters.tier = filterTier;
      if (filterStatus) filters.status = filterStatus;
      
      const userData = await getAllFilmSubscribers(filters);
      setUsers(userData);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filterTier, filterStatus]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleEditClick = (user) => {
    setEditData({
      tier: user.filmSubscriptionTier || FILM_TIERS.FREE,
      status: user.filmSubscriptionStatus || SUBSCRIPTION_STATUS.ACTIVE,
      expiresAt: user.filmSubscriptionExpiresAt ? 
        new Date(user.filmSubscriptionExpiresAt.toDate()).toISOString().split('T')[0] : ''
    });
    setEditDialog({ open: true, user });
  };

  const handleEditSave = async () => {
    try {
      const expiresAt = editData.expiresAt ? new Date(editData.expiresAt) : null;
      
      await updateUserFilmTier(editDialog.user.id, editData.tier, {
        status: editData.status,
        expiresAt: expiresAt
      });
      
      setEditDialog({ open: false, user: null });
      fetchUsers();
      alert('User subscription updated successfully');
    } catch (err) {
      console.error('Error updating user:', err);
      alert('Failed to update user subscription');
    }
  };

  const handleTrialClick = (user) => {
    setTrialData({
      tier: FILM_TIERS.PROFESSIONAL,
      days: 14
    });
    setTrialDialog({ open: true, user });
  };

  const handleTrialGrant = async () => {
    try {
      await grantTrialAccess(trialDialog.user.id, trialData.tier, trialData.days);
      
      setTrialDialog({ open: false, user: null });
      fetchUsers();
      alert(`${trialData.days}-day trial granted successfully`);
    } catch (err) {
      console.error('Error granting trial:', err);
      alert('Failed to grant trial access');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case FILM_TIERS.FREE: return 'default';
      case FILM_TIERS.BASIC: return 'info';
      case FILM_TIERS.PROFESSIONAL: return 'primary';
      case FILM_TIERS.ENTERPRISE: return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE: return 'success';
      case SUBSCRIPTION_STATUS.TRIAL: return 'warning';
      case SUBSCRIPTION_STATUS.EXPIRED: return 'error';
      case SUBSCRIPTION_STATUS.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTier = !filterTier || user.filmSubscriptionTier === filterTier;
    const matchesStatus = !filterStatus || user.filmSubscriptionStatus === filterStatus;
    
    return matchesSearch && matchesTier && matchesStatus;
  });

  if (loading && users.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: brandColors.primary }}>
          Film Subscriptions
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage individual Film subscription tiers and access
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tier</InputLabel>
              <Select
                value={filterTier}
                label="Tier"
                onChange={(e) => setFilterTier(e.target.value)}
              >
                <MenuItem value="">All Tiers</MenuItem>
                {Object.values(FILM_TIERS).map(tier => (
                  <MenuItem key={tier} value={tier}>{FILM_TIER_NAMES[tier]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All Status</MenuItem>
                {Object.values(SUBSCRIPTION_STATUS).map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUsers}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Users ({filteredUsers.length})
          </Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {user.displayName || 'No Name'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={FILM_TIER_NAMES[user.filmSubscriptionTier] || 'Film Free'}
                        color={getTierColor(user.filmSubscriptionTier)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.filmSubscriptionStatus || 'active'}
                        color={getStatusColor(user.filmSubscriptionStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.filmSubscriptionExpiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {(user.enterpriseCompanyId || user.filmCompanyId) ? (
                        <Chip
                          icon={<BusinessIcon />}
                          label="Enterprise"
                          size="small"
                          color="error"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Individual
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(user.lastLogin)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Edit Subscription">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(user)}
                            disabled={Boolean(user.enterpriseCompanyId || user.filmCompanyId)} // Can't edit enterprise members
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Grant Trial">
                          <IconButton
                            size="small"
                            onClick={() => handleTrialClick(user)}
                            disabled={Boolean(user.enterpriseCompanyId || user.filmCompanyId)}
                            color="warning"
                          >
                            <TrialIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredUsers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No users found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tier</InputLabel>
              <Select
                value={editData.tier}
                label="Tier"
                onChange={(e) => setEditData({ ...editData, tier: e.target.value })}
              >
                {Object.values(FILM_TIERS).filter(t => t !== FILM_TIERS.ENTERPRISE).map(tier => (
                  <MenuItem key={tier} value={tier}>{FILM_TIER_NAMES[tier]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editData.status}
                label="Status"
                onChange={(e) => setEditData({ ...editData, status: e.target.value })}
              >
                {Object.values(SUBSCRIPTION_STATUS).map(status => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Expires At"
              type="date"
              value={editData.expiresAt}
              onChange={(e) => setEditData({ ...editData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trial Dialog */}
      <Dialog open={trialDialog.open} onClose={() => setTrialDialog({ open: false, user: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Trial Access</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Trial Tier</InputLabel>
              <Select
                value={trialData.tier}
                label="Trial Tier"
                onChange={(e) => setTrialData({ ...trialData, tier: e.target.value })}
              >
                <MenuItem value={FILM_TIERS.BASIC}>{FILM_TIER_NAMES[FILM_TIERS.BASIC]}</MenuItem>
                <MenuItem value={FILM_TIERS.PROFESSIONAL}>{FILM_TIER_NAMES[FILM_TIERS.PROFESSIONAL]}</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Trial Duration (days)"
              type="number"
              value={trialData.days}
              onChange={(e) => setTrialData({ ...trialData, days: parseInt(e.target.value) })}
              inputProps={{ min: 1, max: 90 }}
            />
            
            <Alert severity="info">
              User will be automatically downgraded to Film Free when trial expires.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button onClick={handleTrialGrant} variant="contained" color="warning">
            Grant Trial
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FilmSubscriptions;

